import React, { useEffect, useRef, useState } from "react";
import {
  SignedIn,
  SignedOut,
  SignInButton,
  UserButton,
  useAuth,
  useUser,
} from "@clerk/clerk-react";
import {
  createDonationSession,
  fetchDonationRequests,
  fetchDonationSummary,
} from "../api/donations";
import { API_BASE } from "../api/client";

const TURNSTILE_SITE_KEY = process.env.TURNSTILE_SITE_KEY || "";
const CLERK_PUBLISHABLE_KEY = process.env.CLERK_PUBLISHABLE_KEY || "";
const IS_LOCALHOST =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

function formatMoney(currency, amount) {
  return `${currency?.toUpperCase()} ${Number(amount).toFixed(2)}`;
}

function RequestCard({ request }) {
  const done = request.is_completed;
  return (
    <div
      className={`rounded-2xl border p-4 text-sm ${
        done
          ? "border-emerald-200 bg-emerald-50/70"
          : "border-white/70 bg-white/70"
      }`}
    >
      <div className="flex items-center justify-between text-xs text-[var(--muted)]">
        <span>Initiator: {request.initiator_name || "Anonymous"}</span>
        {done ? (
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-700">
            ✓ Completed
          </span>
        ) : (
          <span>{formatMoney(request.currency, request.total_amount)}</span>
        )}
      </div>
      <p className="mt-2 text-sm font-semibold text-[var(--ink)]">
        Request: {request.request_text}
      </p>
      <div className="mt-3 space-y-1 text-xs text-[var(--muted)]">
        {request.contributors.map((contrib, index) => (
          <div
            key={`${request.request_key}-${index}`}
            className="flex items-center justify-between"
          >
            <span>{contrib.name || "Anonymous"}</span>
            <span>{formatMoney(request.currency, contrib.amount)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between pt-2 text-[var(--ink)]">
          <span>Total donation</span>
          <span>{formatMoney(request.currency, request.total_amount)}</span>
        </div>
      </div>
    </div>
  );
}

function RequestGroup({ title, requests }) {
  if (requests.length === 0) return null;
  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
        {title}
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        {requests.map((request) => (
          <RequestCard key={request.request_key} request={request} />
        ))}
      </div>
    </div>
  );
}

function DonationSectionWithAuth() {
  const [amount, setAmount] = useState("5");
  const [displayName, setDisplayName] = useState("");
  const [requestText, setRequestText] = useState("");
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [selectedRequestKey, setSelectedRequestKey] = useState("");
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const userEmail = user?.primaryEmailAddress?.emailAddress || "";
  const userName = user?.fullName || user?.username || "";

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    const warmApi = () =>
      fetch(`${API_BASE}/healthz`, {
        cache: "no-store",
        mode: "no-cors",
      }).catch(() => {});

    const load = async (attempt = 0) => {
      try {
        warmApi();
        const [summaryData, requestList] = await Promise.all([
          fetchDonationSummary(),
          fetchDonationRequests(),
        ]);
        if (cancelled) return;
        setSummary(summaryData);
        setRequests(requestList);
        setStatusMessage("");
        setRequestsLoading(false);
      } catch (err) {
        if (cancelled) return;
        const maxAttempts = 4;
        if (attempt < maxAttempts) {
          const nextAttempt = attempt + 1;
          setStatusMessage(
            `Still connecting… (attempt ${nextAttempt + 1} of ${maxAttempts + 1})`
          );
          retryTimer = setTimeout(
            () => load(nextAttempt),
            1500 * Math.pow(2, attempt)
          );
          return;
        }
        setRequestsLoading(false);
        setError(err.message || "Unable to load donations.");
      }
    };
    load();
    const keepAlive = setInterval(warmApi, 60000);
    return () => {
      cancelled = true;
      clearInterval(keepAlive);
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (userName && !displayName) {
      setDisplayName(userName);
    }
  }, [userName, displayName]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || IS_LOCALHOST) {
      return;
    }

    const renderWidget = () => {
      if (!turnstileRef.current) {
        return;
      }
      if (turnstileRef.current.dataset.rendered) {
        return;
      }
      if (window.turnstile) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          "expired-callback": () => setTurnstileToken(""),
          theme: "light",
        });
        turnstileRef.current.dataset.rendered = "true";
      }
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    const scriptId = "turnstile-script";
    const existing = document.getElementById(scriptId);
    if (existing) {
      existing.addEventListener("load", renderWidget, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = renderWidget;
    script.onerror = () => setError("Turnstile failed to load.");
    document.body.appendChild(script);
  }, []);

  const selectedRequest = requests.find(
    (item) => item.request_key === selectedRequestKey
  );
  const resolvedRequestText =
    selectedRequest?.request_text || requestText;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!isSignedIn) {
      setError("Sign in to continue.");
      return;
    }
    if (!turnstileToken && !IS_LOCALHOST && TURNSTILE_SITE_KEY) {
      setError("Complete the Turnstile check before donating.");
      return;
    }
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Unable to verify session. Try again.");
      }
      const payload = {
        amount: Number(amount),
        display_name: displayName.trim() || userName || "Anonymous",
        request_text: resolvedRequestText.trim(),
        request_key: selectedRequest?.request_key || undefined,
        email: userEmail || undefined,
        turnstile_token: turnstileToken || "dev",
      };
      const res = await createDonationSession(payload, token);
      if (res?.url) {
        window.location.href = res.url;
      } else {
        throw new Error("Stripe session not created.");
      }
    } catch (err) {
      setError(err.message || "Payment failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto max-w-6xl px-4 pb-16">
      <div className="glass-card p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
              Support the project
            </p>
            <h2 className="mt-2 text-2xl font-display text-[var(--ink)]">
              Donate and request features
            </h2>
            <p className="mt-2 text-sm text-[var(--muted)]">
              Donations help keep the calculator free. Requests are held
              for manual review and shown publicly after approval. Sign in
              is required and public requests require a $1 minimum.
            </p>
          </div>
          {summary ? (
            <div className="rounded-2xl border border-white/70 bg-white/70 px-4 py-3 text-sm text-[var(--muted)]">
              <div className="text-xs uppercase tracking-[0.2em]">
                Total raised
              </div>
              <div className="mt-1 text-lg font-semibold text-[var(--ink)]">
                {summary.currency?.toUpperCase()} {" "}
                {summary.total_amount?.toFixed(2)}
              </div>
              <div className="text-xs">
                {summary.total_count} donors
              </div>
            </div>
          ) : null}
        </div>

        <form className="mt-6 grid gap-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-xs text-[var(--muted)] md:col-span-2">
              Add to existing request (optional)
              <select
                className="select-field mt-1"
                value={selectedRequestKey}
                onChange={(event) =>
                  setSelectedRequestKey(event.target.value)
                }
              >
                <option value="">Create a new request</option>
                {requests.map((request) => (
                  <option
                    key={request.request_key}
                    value={request.request_key}
                  >
                    {request.request_text} · {request.currency?.toUpperCase()}{" "}
                    {request.total_amount?.toFixed(2)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-[var(--muted)]">
              Display name
              <input
                type="text"
                className="input-field mt-1"
                placeholder="Name shown publicly"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </label>
            <label className="text-xs text-[var(--muted)]">
              Amount (USD)
              <input
                type="number"
                min="1"
                step="1"
                className="input-field mt-1"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </label>
          </div>
          <label className="text-xs text-[var(--muted)]">
            Request (optional)
            <textarea
              rows="3"
              className="input-field mt-1"
              placeholder="Example: no ads, add floor hockey, add weekly email"
              value={selectedRequest?.request_text || requestText}
              disabled={!!selectedRequest}
              onChange={(event) => setRequestText(event.target.value)}
            />
          </label>
          <div className="rounded-2xl border border-white/70 bg-white/70 p-4 text-xs text-[var(--muted)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[var(--ink)]">
                  Sign in required
                </p>
                <p className="mt-1 text-xs text-[var(--muted)]">
                  Requests and donations are tied to a verified account.
                </p>
              </div>
              <SignedIn>
                <div className="flex items-center gap-2 text-xs text-[var(--muted)]">
                  <span>{userEmail || "Signed in"}</span>
                  <UserButton />
                </div>
              </SignedIn>
              <SignedOut>
                <SignInButton
                  mode="modal"
                  forceRedirectUrl="/donate"
                  fallbackRedirectUrl="/donate"
                  signUpForceRedirectUrl="/donate"
                  signUpFallbackRedirectUrl="/donate"
                >
                  <button
                    type="button"
                    className="rounded-full bg-[var(--accent)] px-4 py-2 text-xs font-semibold text-white"
                  >
                    Sign in
                  </button>
                </SignInButton>
              </SignedOut>
            </div>
          </div>

          <div className="rounded-2xl border border-white/70 bg-white/70 p-4 text-xs text-[var(--muted)]">
            <p className="text-[var(--ink)]">
              Human check (required)
            </p>
            {TURNSTILE_SITE_KEY ? (
              <div ref={turnstileRef} className="mt-2" />
            ) : (
              <p className="mt-2 text-xs text-[var(--accent)]">
                Turnstile site key missing. Contact support.
              </p>
            )}
          </div>

          {error ? (
            <p className="text-xs text-[var(--accent)]">{error}</p>
          ) : null}
          {statusMessage && !error && !requestsLoading ? (
            <p className="text-xs text-[var(--muted)]">{statusMessage}</p>
          ) : null}
          <button
            type="submit"
            disabled={loading || !isSignedIn}
            className="inline-flex items-center justify-center rounded-full bg-[var(--accent)] px-5 py-3 text-sm font-semibold text-white shadow-[0_18px_40px_rgba(227,108,79,0.35)] transition hover:-translate-y-0.5 disabled:opacity-60"
          >
            {loading
              ? "Redirecting..."
              : isSignedIn
              ? "Donate with Stripe"
              : "Sign in to donate"}
          </button>
        </form>

        <div className="mt-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
            Requests by support
          </p>
          <div className="mt-3 grid gap-4">
            {requestsLoading ? (
              <div className="flex items-start gap-3 rounded-2xl border border-white/70 bg-white/70 p-4 text-sm text-[var(--muted)]">
                <span className="mt-0.5 h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[var(--accent)] border-t-transparent" />
                <div>
                  <p className="text-[var(--ink)]">Connecting to the database…</p>
                  <p className="mt-1 text-xs">
                    To keep costs down, the database goes to sleep when
                    nobody is around. Waking it up can take up to a minute.
                  </p>
                  {statusMessage ? (
                    <p className="mt-1 text-xs">{statusMessage}</p>
                  ) : null}
                </div>
              </div>
            ) : requests.length === 0 ? (
              <div className="rounded-2xl border border-white/70 bg-white/70 p-4 text-sm text-[var(--muted)]">
                No approved requests yet. Be the first to support the project.
              </div>
            ) : (
              <>
                <RequestGroup
                  title="Outstanding"
                  requests={requests.filter((item) => !item.is_completed)}
                />
                <RequestGroup
                  title="Completed"
                  requests={requests.filter((item) => item.is_completed)}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function DonationSection() {
  if (!CLERK_PUBLISHABLE_KEY) {
    return (
      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="glass-card p-6 md:p-8">
          <h2 className="text-2xl font-display text-[var(--ink)]">
            Donations are temporarily unavailable
          </h2>
          <p className="mt-2 text-sm text-[var(--muted)]">
            Clerk publishable key is missing. Set
            <span className="font-semibold text-[var(--ink)]">
              {" "}CLERK_PUBLISHABLE_KEY{" "}
            </span>
            in the webapp environment variables and redeploy.
          </p>
        </div>
      </section>
    );
  }
  return <DonationSectionWithAuth />;
}
